import React from "react";
import { Column } from "types/Datatable";

type Props = {
  item: any;
  columns: Column[];
  /** The table's own cell renderer, so a card cell looks and behaves like a table cell. */
  renderColumnValue: (column: Column, item: any) => React.ReactNode;
  /** A non-empty value replaces the default white background (a highlighted row). */
  highlightClassName: string;
  /** Tapping the card (outside its buttons and links) calls this; unset when the table has no `rowUrl` or `onRowClick`. */
  onOpen?: () => void;
};

// Buttons and inputs: on a card they are not read as "name - value" but sit in a row at the bottom.
const ACTION_TYPES = new Set<Column["type"]>([
  "actionBtn",
  "downloadurl",
  "specificAction",
  "checkbox",
  "toggleValue",
]);

// A column with no name has nothing to label a line with, so it can only be a control (a select box, a row menu).
// "#" is the row counter, not data.
const isActionColumn = (column: Column) =>
  ACTION_TYPES.has(column.type) || (!column.name && column.key !== "#");

/**
 * One table row drawn for a phone.
 *
 * Which columns it shows:
 * - If any column has `mobileView`, the card keeps its four slots (top left/right, bottom left/right)
 *   and shows nothing else but the action columns.
 * - Otherwise it lists every other column as a line "column name - value", in column order.
 * - Action columns (see ACTION_TYPES) are always in a wrapping row at the bottom, so nothing a desktop
 *   row offers is lost; a column already shown in a slot is not repeated there.
 */
export default function DataTablePhoneCard({
  item,
  columns,
  renderColumnValue,
  highlightClassName,
  onOpen,
}: Props) {
  const hasSlots = columns.some((column) => column.mobileView);
  const slotColumn = (slot: Column["mobileView"]) =>
    columns.find((column) => column.mobileView === slot);
  const topLeft = slotColumn("topLeft");
  const topRight = slotColumn("topRight");
  const bottomLeft = slotColumn("bottomLeft");
  const bottomRight = slotColumn("bottomRight");
  const slotted = [topLeft, topRight, bottomLeft, bottomRight];

  const actionColumns = columns.filter(
    (column) => isActionColumn(column) && !slotted.includes(column)
  );
  const lineColumns = hasSlots
    ? []
    : columns.filter((column) => column.key !== "#" && !isActionColumn(column));

  // Enter or Space on the card itself opens it; on a button or link inside, the key stays that element's.
  const openOnKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onOpen?.();
  };

  return (
    <div
      role="listitem"
      className={`border border-[#E6E1DB] p-4 mb-3 rounded-sm ${
        highlightClassName || "bg-white"
      } ${onOpen ? "cursor-pointer" : ""}`}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? openOnKey : undefined}
    >
      {hasSlots ? (
        <>
          {/* The left cell wraps a long title, even in the middle of a word, but keeps at least 6rem:
              without the floor a wide right cell would squeeze a short date or number to one character
              per line. The right cell has no `min-w-0`, so it keeps the width of its content (an amount,
              or a number or badge marked `whitespace-nowrap`) and breaks only where it may. */}
          <div className="flex justify-between gap-3 mb-2">
            <div className="min-w-24 [overflow-wrap:anywhere]">{topLeft ? renderColumnValue(topLeft, item) : null}</div>
            <div>{topRight ? renderColumnValue(topRight, item) : null}</div>
          </div>
          <div className="flex justify-between gap-3">
            <div className="min-w-24 [overflow-wrap:anywhere]">{bottomLeft ? renderColumnValue(bottomLeft, item) : null}</div>
            <div>{bottomRight ? renderColumnValue(bottomRight, item) : null}</div>
          </div>
        </>
      ) : (
        <div className="space-y-1.5 text-sm">
          {lineColumns.map((column) => (
            <div
              key={column.key}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="shrink-0 text-gray-500">{column.name}</span>
              <span className="min-w-0 break-words text-right text-gray-900">
                {renderColumnValue(column, item)}
              </span>
            </div>
          ))}
        </div>
      )}
      {actionColumns.length > 0 && (
        // Buttons here must not also open the card, so the row swallows the click.
        <div
          className="mt-3 flex flex-wrap items-start gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          {actionColumns.map((column) => {
            // A label around the checkbox makes the whole 44 px cell tappable.
            const Cell = column.type === "checkbox" ? "label" : "div";
            return (
              <Cell
                key={column.key}
                className="flex min-h-11 min-w-11 flex-col items-center justify-center gap-0.5 text-xs text-gray-500"
              >
                {renderColumnValue(column, item)}
                {column.name}
              </Cell>
            );
          })}
        </div>
      )}
    </div>
  );
}
